import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Html from '@components/page/Html';
import Masonry from '@components/page/Masonry';
import DirectoryMasonry from '@components/page/DirectoryMasonry';
import VideoPlayer from '@components/page/VideoPlayer';
import VideoEmbed from '@components/page/VideoEmbed';
import Link from '@components/shared/Link';
import { getBestAvailableUrl } from '@components/shared/OptimizedImage';
import DeletedImagePlaceholder from '@components/shared/DeletedImagePlaceholder';
import { BLOCK_TYPES, GALLERY_LAYOUTS, ITEM_TYPES, galleryImagesFromItems } from '@/blocks/schema';
import { BLOCK_GALLERY_MESSAGE } from '@/blocks/preview';
import { cssSpace, parseSpace } from '@/blocks/spacing';
import { cellFilledAny, cellStyle, columnsStyle, columnsWidthCss, flowStyle } from '@/blocks/columns';
import { blockForLayer, deviceCss } from '@/blocks/responsive';
import useTextDevice from '@/hooks/useTextDevice';
import { cssBackdropFilter, cssFilter } from '@/blocks/filter';
import { resolveItemTheme } from '@/blocks/media';
import { buildVectorStyle } from '@/theme/logo';
import FilteredMark from '@components/shared/FilteredMark';
import { getPagePath } from '@/utils/pagePaths';
import { useBlockEditing, useEditorFrame } from '@components/preview/editGates';
import { useAppContext } from '@/context/AppContext';
import { previewLibrary } from '@/preview/hoverChrome';
import { blockLook } from '@/blocks/look';

/** Caption and colour overrides for one block, as wrapper style plus any @font-face. */
function useViewedBlock(block) {
    const device = useTextDevice();
    return blockForLayer(block, device === 'desktop' ? 'base' : device);
}

function DeviceStyles({ block }) {
    const { content, theme } = useAppContext();
    const css = deviceCss(block, previewLibrary(content), theme);
    if (!css) return null;
    return <style>{css}</style>;
}

DeviceStyles.propTypes = {
    block: PropTypes.object,
};

function useBlockLook(block) {
    const { content, theme } = useAppContext();
    return blockLook(block, previewLibrary(content), theme);
}

function wrapperStyle(block, look) {
    const spacing = blockSpacing(block);
    if (!Object.keys(look.vars).length) return spacing;
    return { ...(spacing || {}), ...look.vars };
}

function LookFaces({ look }) {
    return look.fontFaceCss ? <style>{look.fontFaceCss}</style> : null;
}

LookFaces.propTypes = {
    look: PropTypes.object.isRequired,
};

function OverlayShell({ block, id, editing, children }) {
    const look = useBlockLook(block);
    const viewed = useViewedBlock(block);
    return (
        <div
            className="w-full"
            data-page-path="page.block"
            data-page-id={id}
            data-block-editing={editing ? '1' : undefined}
            style={wrapperStyle(block, look)}
            {...spaceAttrs(viewed)}
        >
            <LookFaces look={look} />
            <DeviceStyles block={block} />
            {children}
        </div>
    );
}

OverlayShell.propTypes = {
    block: PropTypes.object.isRequired,
    id: PropTypes.string,
    editing: PropTypes.bool,
    children: PropTypes.node,
};

function blockSpacing(block) {
    const top = cssSpace(block?.marginTop);
    const bottom = cssSpace(block?.marginBottom);
    const ownsFilters = block?.type === BLOCK_TYPES.VECTOR;
    const backdropOnGlyphs = (
        ownsFilters || block?.type === BLOCK_TYPES.RICHTEXT
    ) && cssBackdropFilter(block?.filter);
    // A filter on an ancestor isolates the backdrop, so a glyph invert would
    // no longer see the page behind it. Vectors paint their own filter.
    const filter = ownsFilters || backdropOnGlyphs ? undefined : cssFilter(block?.filter);
    // Vectors and text invert through their shapes. A backdrop filter on this
    // box would also flip the empty space around them.
    const backdrop = ownsFilters || backdropOnGlyphs ? undefined : cssBackdropFilter(block?.filter);
    if (!top && !bottom && !filter && !backdrop) return undefined;
    // Spacing is padding so the block highlight encloses it and the preview
    // bridge can shade / drag it as a padding region. Filter restyles the
    // block. Backdrop invert restyles whatever is behind the block.
    return {
        ...(top ? { paddingTop: top } : {}),
        ...(bottom ? { paddingBottom: bottom } : {}),
        ...(filter ? { filter } : {}),
        ...(backdrop ? { backdropFilter: backdrop, WebkitBackdropFilter: backdrop } : {}),
    };
}

function spaceAttrs(block) {
    const top = parseSpace(block?.marginTop);
    const bottom = parseSpace(block?.marginBottom);
    return {
        'data-space-top': String(top.value),
        'data-space-top-unit': top.unit,
        'data-space-bottom': String(bottom.value),
        'data-space-bottom-unit': bottom.unit,
    };
}

function postGalleryEdit(blockId, edit) {
    if (typeof window === 'undefined' || window.parent === window) return;
    window.parent.postMessage({
        type: BLOCK_GALLERY_MESSAGE,
        blockId,
        edit,
    }, window.location.origin);
}

/** Shown only in the editor preview, so an empty block still has something to click. */
function EmptyBlock({ children }) {
    const inFrame = useEditorFrame();
    if (!inFrame) return null;
    return (
        <div className="page-column px-4 py-8 text-center text-sm opacity-50">
            {children}
        </div>
    );
}

EmptyBlock.propTypes = {
    children: PropTypes.node,
};

/** One block in the stack. In the editor preview it is editable only once entered. */
function StackBlock(props) {
    const editing = useBlockEditing(props.block.id);
    const look = useBlockLook(props.block);
    const viewed = useViewedBlock(props.block);
    return (
        <div
            className="w-full shrink-0"
            data-page-path="page.block"
            data-page-id={props.block.id || ''}
            data-block-editing={editing ? '1' : undefined}
            style={wrapperStyle(props.block, look)}
            {...spaceAttrs(viewed)}
        >
            <LookFaces look={look} />
            <DeviceStyles block={props.block} />
            <Block {...props} editing={editing} />
        </div>
    );
}

StackBlock.propTypes = {
    block: PropTypes.object.isRequired,
};

/**
 * A block in a box on a slideshow sheet. In the editor preview it is editable only once entered.
 * An inert copy (a slide that is out of view or fading out) is not a block the editor can find.
 */
export function BoxBlock({ inert = false, anchor = true, ...props }) {
    const editing = useBlockEditing(inert ? '' : props.block.id);
    const look = useBlockLook(props.block);
    const found = anchor && !inert;
    return (
        <div
            className="w-full"
            data-page-path={found ? 'page.block' : undefined}
            data-page-id={found ? props.block.id || '' : undefined}
            data-block-editing={editing ? '1' : undefined}
            style={wrapperStyle(props.block, look)}
        >
            <LookFaces look={look} />
            <DeviceStyles block={props.block} />
            <Block {...props} editing={editing} inBox />
        </div>
    );
}

BoxBlock.propTypes = {
    block: PropTypes.object.isRequired,
    inert: PropTypes.bool,
    /** False when the box around it already stands for the block in the editor, so there is one frame, not two. */
    anchor: PropTypes.bool,
    /** Text typed into the block outside the editor frame. */
    onTextEdit: PropTypes.func,
};

/**
 * Renders a page's blocks.
 *
 * Every block type delegates to the component that already rendered that content,
 * so a page compiled from legacy fields produces the same output it always did.
 * The new item kinds (video, embeds, page tiles mixed into a grid) are the only
 * things rendered here directly.
 *
 * Slideshow is handled by the caller, because it owns the whole viewport and its
 * own text overlay rather than sitting in a vertical stack.
 */
function BlockRenderer({ blocks, page, pages, files, theme, fullPagePath }) {
    if (!Array.isArray(blocks) || blocks.length === 0) return null;

    return blocks.map((block, index) => (
        <StackBlock
            key={block.id || `${block.type}-${index}`}
            block={block}
            page={page}
            pages={pages}
            files={files}
            theme={theme}
            fullPagePath={fullPagePath}
        />
    ));
}

function RichTextFrame({ block, children }) {
    const viewed = useViewedBlock(block);
    if (!cssBackdropFilter(viewed?.filter)) return children;
    const filter = cssFilter(viewed?.filter);
    return (
        <div
            className="block-invert-text"
            style={{
                mixBlendMode: 'difference',
                color: '#fff',
                ...(filter ? { filter } : {}),
            }}
        >
            {children}
        </div>
    );
}

RichTextFrame.propTypes = {
    block: PropTypes.object.isRequired,
    children: PropTypes.node,
};

/** Text that flows into columns, per device. */
function TextFlow({ block, children }) {
    const vars = flowStyle(block);
    if (!vars) return children;
    return <div className="text-flow" style={vars}>{children}</div>;
}

TextFlow.propTypes = {
    block: PropTypes.object.isRequired,
    children: PropTypes.node,
};

/** Texts side by side; a column with its own colours paints its background out to the gaps. */
function ColumnsBlock({ block, page, pages, files, theme, fullPagePath, editing, inBox = false }) {
    const { theme: siteTheme } = useAppContext();
    const palettes = siteTheme || theme;
    return (
        // Boxed like a text's .html-page, so the same width in ch puts the outer
        // columns' text on the same edges as a text block's. In a box, the box is the width.
        <div
            className={inBox ? 'text-columns-frame w-full' : 'text-columns-frame mx-auto px-4 w-full'}
            style={inBox ? undefined : { maxWidth: columnsWidthCss(block) }}
        >
            <div className="text-columns" style={columnsStyle(block)}>
                {(block.cells || []).map((cell, i) => (
                    <div
                        key={cell.id || i}
                        className="text-column"
                        data-column-id={cell.id || undefined}
                        data-fill={cellFilledAny(block, cell.id, palettes) ? '' : undefined}
                        style={cellStyle(block, cell.id, palettes)}
                    >
                        <Block
                            block={cell}
                            page={page}
                            pages={pages}
                            files={files}
                            theme={theme}
                            fullPagePath={fullPagePath}
                            editing={editing}
                            inColumn
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

ColumnsBlock.propTypes = {
    block: PropTypes.object.isRequired,
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
    fullPagePath: PropTypes.string,
    editing: PropTypes.bool,
    inBox: PropTypes.bool,
};

const BOX_VECTOR_WIDTH = { value: 100, unit: '%' };

/**
 * One block. In a column it fills its column; in a box on a slideshow sheet
 * (inBox) it fills the box, which sets its width.
 */
export function Block({ block, page, pages, files, theme, fullPagePath, editing = false, inColumn = false, inBox = false, onTextEdit }) {
    switch (block.type) {
        case BLOCK_TYPES.RICHTEXT:
            return (
                <RichTextFrame block={block}>
                    <TextFlow block={block}>
                        <Html
                            content={block.html}
                            editing={editing}
                            onEdit={onTextEdit}
                            editPath={block.id ? `blocks.${block.id}.html` : 'content'}
                            contentMaxWidth={block.maxWidth}
                            fill={inColumn || inBox}
                            name={page?.name}
                            pages={pages}
                            files={files}
                            gallery={block.galleryContext || 'none'}
                        />
                    </TextFlow>
                </RichTextFrame>
            );

        case BLOCK_TYPES.GALLERY:
            return (
                <GalleryBlock
                    block={block}
                    page={page}
                    pages={pages}
                    files={files}
                    theme={theme}
                    fullPagePath={fullPagePath}
                    editing={editing}
                />
            );

        case BLOCK_TYPES.EMBED: {
            const embed = block.embed || {};
            if (!embed.videoId) return <EmptyBlock>Edit this block to add a YouTube or Vimeo link.</EmptyBlock>;
            const poster = block.posterFileId ? getBestAvailableUrl(files?.[block.posterFileId], 1280) : null;
            const video = (
                <VideoEmbed
                    provider={embed.provider}
                    videoId={embed.videoId}
                    start={embed.start}
                    poster={poster ? withLeadingSlash(poster) : null}
                    title={block.title || page?.name}
                />
            );
            return inBox ? video : <div className="page-column px-4">{video}</div>;
        }

        case BLOCK_TYPES.COLUMNS:
            return (
                <ColumnsBlock
                    block={block}
                    page={page}
                    pages={pages}
                    files={files}
                    theme={theme}
                    fullPagePath={fullPagePath}
                    editing={editing}
                    inBox={inBox}
                />
            );

        case BLOCK_TYPES.SPACER:
            return <div aria-hidden="true" data-spacer="" style={{ height: `${block.height ?? 4}vh` }} />;

        case BLOCK_TYPES.UNLAYER:
            // Stored HTML from the optional landing-page builder.
            return block.html
                ? <div className="w-full unlayer-block" dangerouslySetInnerHTML={{ __html: block.html }} />
                : null;

        case BLOCK_TYPES.VECTOR:
            return (
                <VectorBlock
                    block={inBox ? { ...block, width: BOX_VECTOR_WIDTH, height: 'auto' } : block}
                    pages={pages}
                    theme={theme}
                />
            );

        default:
            return null;
    }
}

/**
 * A gallery block.
 *
 * Images and spreads go through the existing Masonry, and page tiles through
 * DirectoryMasonry, so those paths are unchanged. Video and embeds are rendered
 * alongside them, which is what makes a mixed grid possible.
 */
function GalleryBlock({ block, page, pages, files, theme, fullPagePath, editing = false }) {
    const viewed = useViewedBlock(block);
    const items = block.items || [];
    const layout = block.layout || GALLERY_LAYOUTS.MASONRY;

    const mediaItems = items.filter((item) => (
        item.type === ITEM_TYPES.VIDEO || item.type === ITEM_TYPES.YOUTUBE
    ));
    const imageItems = items.filter((item) => (
        item.type === ITEM_TYPES.IMAGE || item.type === ITEM_TYPES.SPREAD
    ));
    const pageItems = items.filter((item) => item.type === ITEM_TYPES.PAGE);

    // Directory tiles come from links, plus any page items added to a mixed grid.
    const links = [
        ...(block.links || []),
        ...pageItems.map((item) => ({
            pageId: item.pageId,
            description: item.description || '',
            imageIds: item.imageIds || [],
            cropRatio: item.cropRatio,
            cycleDuration: item.cycleDuration,
            crops: item.crops || {},
        })),
    ];

    const inFrame = useEditorFrame();
    const showsImages = imageItems.length > 0 && layout !== GALLERY_LAYOUTS.DIRECTORY;
    const dropImages = inFrame && layout !== GALLERY_LAYOUTS.DIRECTORY;
    if (!showsImages && links.length === 0 && mediaItems.length === 0 && !dropImages) {
        return (
            <EmptyBlock>
                {layout === GALLERY_LAYOUTS.DIRECTORY
                    ? 'Empty directory. Edit this block to add page tiles.'
                    : 'Empty grid. Edit this block to add images.'}
            </EmptyBlock>
        );
    }

    const arrangeImages = editing
        ? (ids) => postGalleryEdit(block.id, { action: 'order', kind: 'image', ids })
        : undefined;
    const arrangeLinks = editing
        ? (ids) => postGalleryEdit(block.id, { action: 'order', kind: 'link', ids })
        : undefined;
    const tileEdit = editing
        ? (edit) => postGalleryEdit(block.id, edit)
        : undefined;

    return (
        <>
            {editing && (
                <p className="page-column px-4 pb-2 text-center text-[11px] text-gray-500">
                    Drag {layout === GALLERY_LAYOUTS.DIRECTORY ? 'a tile' : 'an image'} onto another to place it before that one.
                    {layout !== GALLERY_LAYOUTS.DIRECTORY ? ' Tick images to caption, remove or move several at once.' : ''}
                </p>
            )}
            {/* Masonry resolves file ids from context itself, so it is handed the
                same gallery.images shape the page route always passed it. */}
            {(showsImages || dropImages) && (
                <Masonry
                    images={galleryImagesFromItems(imageItems)}
                    url={fullPagePath}
                    columns={block.columns}
                    breakpoints={block.breakpoints}
                    gap={block.gap}
                    devices={block.devices}
                    crops={block.crops}
                    arrange={editing}
                    onArrange={arrangeImages}
                    onTileEdit={tileEdit}
                    onFileDrop={(payload) => postGalleryEdit(block.id, {
                        action: 'ui-upload',
                        files: payload.files,
                        beforeId: payload.beforeId || null,
                        keys: payload.keys || [],
                    })}
                />
            )}

            {links.length > 0 && (
                <DirectoryMasonry
                    links={links}
                    columns={block.columns}
                    breakpoints={block.breakpoints}
                    gap={block.gap}
                    devices={block.devices}
                    crossfade={viewed.cycleCrossfade}
                    arrange={editing}
                    onArrange={arrangeLinks}
                    onTileEdit={tileEdit}
                />
            )}

            {mediaItems.length > 0 && (
                <div
                    className="page-column px-4 grid gap-4"
                    style={{ gridTemplateColumns: `repeat(${Math.min(block.columns || 2, 3)}, minmax(0, 1fr))` }}
                >
                    {mediaItems.map((item, index) => {
                        const itemTheme = resolveItemTheme(item, page, theme);
                        const style = {
                            backgroundColor: itemTheme.background,
                            color: itemTheme.text,
                        };

                        if (item.type === ITEM_TYPES.YOUTUBE) {
                            const poster = item.posterFileId
                                ? getBestAvailableUrl(files?.[item.posterFileId], 1280)
                                : null;
                            return (
                                <div key={item.videoId || index} style={style}>
                                    <VideoEmbed
                                        provider={item.provider}
                                        videoId={item.videoId}
                                        start={item.start}
                                        poster={poster ? withLeadingSlash(poster) : null}
                                        title={item.description || page?.name}
                                    />
                                </div>
                            );
                        }

                        const file = files?.[item.fileId];
                        const src = file?.url?.original;
                        if (!src) return null;
                        if (file.isDeleted) {
                            return (
                                <div key={item.fileId || index} style={style}>
                                    <DeletedImagePlaceholder
                                        thumbnailUrl={file.thumbnailUrl}
                                        deletedAt={file.deletedAt}
                                        width={16}
                                        height={9}
                                    />
                                </div>
                            );
                        }
                        const poster = item.posterFileId
                            ? getBestAvailableUrl(files?.[item.posterFileId], 1280)
                            : null;

                        return (
                            <div key={item.fileId || index} style={style}>
                                <VideoPlayer
                                    src={withLeadingSlash(src)}
                                    poster={poster ? withLeadingSlash(poster) : null}
                                    className="w-full"
                                    style={{ aspectRatio: '16 / 9' }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}

function withLeadingSlash(url) {
    if (!url) return url;
    return url.startsWith('/') || url.startsWith('http') ? url : `/${url}`;
}

const ALIGN_JUSTIFY = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};

/** Brand SVG (or override) as a block — mask-tinted or original colours. */
export function VectorBlock({ block, pages, theme }) {
    const viewed = useViewedBlock(block);
    const { src, width, height, tint, style } = buildVectorStyle(viewed, theme?.brand?.logo);
    const align = viewed.align || 'center';
    const alt = block.alt || '';
    const autoHeight = height === 'auto';
    const [ratio, setRatio] = useState(null);
    let href = null;
    if (block.hrefPageId && pages?.[block.hrefPageId]) {
        const path = getPagePath(pages[block.hrefPageId], pages);
        href = path ? `/${path}` : '/';
    }

    useEffect(() => {
        if (!autoHeight || !src) return undefined;
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setRatio(img.naturalWidth / img.naturalHeight);
            }
        };
        img.src = src;
        return () => { cancelled = true; };
    }, [src, autoHeight]);

    // Size from the asset's aspect ratio (measured on load). Tint paints a masked
    // layer; original colours use the <img> directly.
    const graphic = (
        <FilteredMark
            src={src}
            tint={tint}
            color={style.backgroundColor}
            filter={viewed.filter}
            alt={alt}
            maskSize="100% 100%"
            maskPosition="center"
            className="vector-block-graphic inline-block shrink-0"
            imageClassName="block w-full h-auto"
            imageStyle={{ height: autoHeight ? 'auto' : height }}
            style={{
                width,
                height: autoHeight ? undefined : height,
                // Fallback ~3:1 until the asset reports its real ratio (avoids a
                // zero-height flash / permanent collapse if measurement is slow).
                aspectRatio: autoHeight ? String(ratio || 3) : undefined,
            }}
        />
    );

    return (
        <div
            className="vector-block-row relative w-full flex justify-center overflow-visible"
            style={{ justifyContent: ALIGN_JUSTIFY[align] || ALIGN_JUSTIFY.center }}
        >
            {href ? (
                <Link to={href} className="inline-block shrink-0 hover:opacity-80 transition-opacity">
                    {graphic}
                </Link>
            ) : graphic}
        </div>
    );
}

VectorBlock.propTypes = {
    block: PropTypes.object.isRequired,
    pages: PropTypes.object,
    theme: PropTypes.object,
};

/**
 * Fullscreen / slideshow overlay stack: richtext and vector blocks in order.
 * Falls back to page.content Html when the page has no overlay block list yet.
 */
export function OverlayBlocks({
    page,
    pages,
    files,
    theme,
    editing = false,
    editPath = 'content',
    gallery = 'background',
    displayHtml = null,
}) {
    const stored = Array.isArray(page?.blocks)
        ? page.blocks.filter(
            (block) => block.type === BLOCK_TYPES.RICHTEXT || block.type === BLOCK_TYPES.VECTOR,
        )
        : null;

    if (stored) {
        const hasText = stored.some((block) => block.type === BLOCK_TYPES.RICHTEXT);
        const legacyContent = String(page?.content || '').trim();
        // Vectors saved without a text row still need page.content until cleared.
        const rows = (!hasText && legacyContent)
            ? [
                {
                    id: 'legacy-richtext',
                    type: BLOCK_TYPES.RICHTEXT,
                    html: page.content,
                    maxWidth: page?.contentMaxWidth,
                },
                ...stored,
            ]
            : stored;
        if (!rows.length) return null;
        return rows.map((block, index) => {
            const id = block.id || `${block.type}-${index}`;
            if (block.type === BLOCK_TYPES.VECTOR) {
                return (
                    <OverlayShell key={id} block={block} id={id}>
                        <VectorBlock block={block} pages={pages} theme={theme} />
                    </OverlayShell>
                );
            }
            const isPrimary = index === rows.findIndex((entry) => entry.type === BLOCK_TYPES.RICHTEXT);
            return (
                <OverlayShell key={id} block={block} id={id} editing={Boolean(editing && isPrimary)}>
                    <RichTextFrame block={block}>
                        <TextFlow block={block}>
                            <Html
                                content={displayHtml ? displayHtml(block.html) : block.html}
                                editHtml={displayHtml ? block.html : undefined}
                                contentMaxWidth={block.maxWidth ?? page?.contentMaxWidth}
                                name={page?.name}
                                pages={pages}
                                files={files}
                                gallery={gallery}
                                editing={editing && isPrimary ? editing : undefined}
                                editPath={isPrimary ? editPath : `blocks.${id}.html`}
                            />
                        </TextFlow>
                    </RichTextFrame>
                </OverlayShell>
            );
        });
    }

    const vectors = (page?.blocks || []).filter((block) => block.type === BLOCK_TYPES.VECTOR);
    return (
        <>
            {vectors.map((block, index) => (
                <OverlayShell key={block.id || `vector-${index}`} block={block} id={block.id || ''}>
                    <VectorBlock block={block} pages={pages} theme={theme} />
                </OverlayShell>
            ))}
            {(page?.content != null && String(page.content).trim() !== '') && (
                <Html
                    content={page.content}
                    contentMaxWidth={page.contentMaxWidth}
                    name={page?.name}
                    pages={pages}
                    files={files}
                    gallery={gallery}
                    editing={editing || undefined}
                    editPath={editPath}
                />
            )}
        </>
    );
}

/** @deprecated Use OverlayBlocks */
export function OverlayVectorBlocks(props) {
    return <OverlayBlocks {...props} />;
}

OverlayBlocks.propTypes = {
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
    editing: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    editPath: PropTypes.string,
    gallery: PropTypes.string,
    /** Display-only rewrite of each text's HTML; the editor still edits the stored HTML. */
    displayHtml: PropTypes.func,
};

OverlayVectorBlocks.propTypes = OverlayBlocks.propTypes;

BlockRenderer.propTypes = {
    blocks: PropTypes.array,
    page: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    theme: PropTypes.object,
    fullPagePath: PropTypes.string,
};

export default BlockRenderer;
